// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, Divider, Collapse, CardMedia } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import { B1Tiny, B3Tiny, ModalActionsLoadingButton, ModalContentSwitch } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const MONITORING_PANEL_HELP_IMAGE = "/illustrations/converted/monitoring/monitoring-panel.png";
const MONITORING_PANEL_HELP_DESCRIPTION = (
  <>
    To output the current drawdown and performance values of the current trading system and for each individual trading module, the so-called
    inspector panel can be optionally enabled. If enabled, you can easily evaluate the current values either for the entire trading system or for a
    trading module individually simply by entering the module number in the text field of the inspector panel.
    <br />
    <br />
    <b>
      To show the value for all modules combined, enter ALL. To reset the value for the entire trading system or for a module individually, enter R or
      R followed by the module number, e.g. R4675.
    </b>
    <br />
    <br />
    As soon as any of the integrated trading modules requires the display of the inspector panel, it is shown. Therefore, only if <b>all</b> trading
    modules have this feature disabled, the inspector panel is not shown. By default it is hidden.
  </>
);

const MONITORING_PERFORMANCE_HELP_IMAGE = "/illustrations/converted/monitoring/monitoring-performance.png";
const MONITORING_PERFORMANCE_HELP_DESCRIPTION = (
  <>
    The monitoring engine can be configured to halt trading once a specified performance limit is reached using the input parameters outlined in the
    section below. These parameters establish the absolute and relative upper and lower limits. A limit can be enabled or disabled with the on/off
    switch.
    <br />
    <br />
    Separate input parameters exist for the upper and lower permissible performance results. This allows the application to suspend the initiation of
    new trades when positive or negative performance results are achieved. The diagram below illustrates the functional principle of performance
    monitoring.
    <br />
    <br />
    The limits can be configured to monitor the performance of various time periods such as daily, weekly, monthly, yearly, and overall performance.{" "}
    <b>
      If the performance reaches the upper or lower limit within its period, the ability to open new trades will be temporarily suspended. At the
      start of a new period, the performance value is reset, and trading resumes
    </b>
    .
  </>
);

const MONITORING_DRAWDOWN_HELP_IMAGE = "/illustrations/converted/monitoring/monitoring-drawdown.png";
const MONITORING_DRAWDOWN_HELP_DESCRIPTION = (
  <>
    The monitoring engine can be configured to halt trading once a specified drawdown limit is reached using the input parameters outlined in the
    section below. These parameters establish the absolute and relative upper limits. A limit can be enabled or disabled with the on/off switch.
    <br />
    <br />
    This allows the application to suspend the initiation of new trades when excessive drawdown levels are reached. The diagram below illustrates the
    functional principle of drawdown monitoring. By design, a drawdown is a positive value, therefore the limit values must be greater than 0 as well.
    <br />
    <br />
    The limits can be configured to monitor the drawdown of various time periods such as daily, weekly, monthly, yearly, and overall drawdown.{" "}
    <b>
      If the drawdown reaches the upper limit within its period, the ability to open new trades will be temporarily suspended. At the start of a new
      period, the drawdown value is reset, and trading resumes
    </b>
    .
  </>
);

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const MonitoringDefaults = {
  nDrawInspectorPanel: false,
  //
  dPerformanceDayAbsMin: "0.00",
  dPerformanceDayAbsMax: "0.00",
  dPerformanceDayRelMin: "0.00",
  dPerformanceDayRelMax: "0.00",
  dPerformanceWeekAbsMin: "0.00",
  dPerformanceWeekAbsMax: "0.00",
  dPerformanceWeekRelMin: "0.00",
  dPerformanceWeekRelMax: "0.00",
  dPerformanceMonthAbsMin: "0.00",
  dPerformanceMonthAbsMax: "0.00",
  dPerformanceMonthRelMin: "0.00",
  dPerformanceMonthRelMax: "0.00",
  dPerformanceYearAbsMin: "0.00",
  dPerformanceYearAbsMax: "0.00",
  dPerformanceYearRelMin: "0.00",
  dPerformanceYearRelMax: "0.00",
  dPerformanceSinceStartAbsMin: "0.00",
  dPerformanceSinceStartAbsMax: "0.00",
  dPerformanceSinceStartRelMin: "0.00",
  dPerformanceSinceStartRelMax: "0.00",
  //
  dDrawdownDayAbsMax: "0.00",
  dDrawdownDayRelMax: "0.00",
  dDrawdownWeekAbsMax: "0.00",
  dDrawdownWeekRelMax: "0.00",
  dDrawdownMonthAbsMax: "0.00",
  dDrawdownMonthRelMax: "0.00",
  dDrawdownYearAbsMax: "0.00",
  dDrawdownYearRelMax: "0.00",
  dDrawdownSinceStartAbsMax: "0.00",
  dDrawdownSinceStartRelMax: "0.00",
  // Enable switches for each parameter
  bPerformanceDayAbsMin: false,
  bPerformanceDayAbsMax: false,
  bPerformanceDayRelMin: false,
  bPerformanceDayRelMax: false,
  bPerformanceWeekAbsMin: false,
  bPerformanceWeekAbsMax: false,
  bPerformanceWeekRelMin: false,
  bPerformanceWeekRelMax: false,
  bPerformanceMonthAbsMin: false,
  bPerformanceMonthAbsMax: false,
  bPerformanceMonthRelMin: false,
  bPerformanceMonthRelMax: false,
  bPerformanceYearAbsMin: false,
  bPerformanceYearAbsMax: false,
  bPerformanceYearRelMin: false,
  bPerformanceYearRelMax: false,
  bPerformanceSinceStartAbsMin: false,
  bPerformanceSinceStartAbsMax: false,
  bPerformanceSinceStartRelMin: false,
  bPerformanceSinceStartRelMax: false,
  bDrawdownDayAbsMax: false,
  bDrawdownDayRelMax: false,
  bDrawdownWeekAbsMax: false,
  bDrawdownWeekRelMax: false,
  bDrawdownMonthAbsMax: false,
  bDrawdownMonthRelMax: false,
  bDrawdownYearAbsMax: false,
  bDrawdownYearRelMax: false,
  bDrawdownSinceStartAbsMax: false,
  bDrawdownSinceStartRelMax: false,
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  // Performance parameters
  dPerformanceDayAbsMin: { digits: 2, step: 0.1, max: 0.0 },
  dPerformanceDayAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dPerformanceDayRelMin: { digits: 2, step: 0.01, max: 0.0 },
  dPerformanceDayRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dPerformanceWeekAbsMin: { digits: 2, step: 0.1, max: 0.0 },
  dPerformanceWeekAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dPerformanceWeekRelMin: { digits: 2, step: 0.01, max: 0.0 },
  dPerformanceWeekRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dPerformanceMonthAbsMin: { digits: 2, step: 0.1, max: 0.0 },
  dPerformanceMonthAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dPerformanceMonthRelMin: { digits: 2, step: 0.01, max: 0.0 },
  dPerformanceMonthRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dPerformanceYearAbsMin: { digits: 2, step: 0.1, max: 0.0 },
  dPerformanceYearAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dPerformanceYearRelMin: { digits: 2, step: 0.01, max: 0.0 },
  dPerformanceYearRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dPerformanceSinceStartAbsMin: { digits: 2, step: 0.1, max: 0.0 },
  dPerformanceSinceStartAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dPerformanceSinceStartRelMin: { digits: 2, step: 0.01, max: 0.0 },
  dPerformanceSinceStartRelMax: { digits: 2, step: 0.01, min: 0.0 },
  // Drawdown parameter
  dDrawdownDayAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dDrawdownDayRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dDrawdownWeekAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dDrawdownWeekRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dDrawdownMonthAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dDrawdownMonthRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dDrawdownYearAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dDrawdownYearRelMax: { digits: 2, step: 0.01, min: 0.0 },
  dDrawdownSinceStartAbsMax: { digits: 2, step: 0.1, min: 0.0 },
  dDrawdownSinceStartRelMax: { digits: 2, step: 0.01, min: 0.0 },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function MonitoringRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get display values
  const getDisplayValue = (key) => config[key];

  const getDrawInspectorPanelDisplay = () => {
    const value = config.nDrawInspectorPanel;
    return value === true ? "On" : "Off";
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Draw inspector panel", getDrawInspectorPanelDisplay())}

      {professionalMode === true ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {/* Daily performance */}
          {config.bPerformanceDayAbsMin === true
            ? renderParam("Daily performance: Absolute lower limit", getDisplayValue("dPerformanceDayAbsMin"))
            : renderParam("Daily performance: Absolute lower limit", "Off")}
          {config.bPerformanceDayAbsMax === true
            ? renderParam("Daily performance: Absolute upper limit", getDisplayValue("dPerformanceDayAbsMax"))
            : renderParam("Daily performance: Absolute upper limit", "Off")}
          {config.bPerformanceDayRelMin === true
            ? renderParam("Daily performance: Relative lower limit", getDisplayValue("dPerformanceDayRelMin"))
            : renderParam("Daily performance: Relative lower limit", "Off")}
          {config.bPerformanceDayRelMax === true
            ? renderParam("Daily performance: Relative upper limit", getDisplayValue("dPerformanceDayRelMax"))
            : renderParam("Daily performance: Relative upper limit", "Off")}

          {/* Weekly performance */}
          {config.bPerformanceWeekAbsMin === true
            ? renderParam("Weekly performance: Absolute lower limit", getDisplayValue("dPerformanceWeekAbsMin"))
            : renderParam("Weekly performance: Absolute lower limit", "Off")}
          {config.bPerformanceWeekAbsMax === true
            ? renderParam("Weekly performance: Absolute upper limit", getDisplayValue("dPerformanceWeekAbsMax"))
            : renderParam("Weekly performance: Absolute upper limit", "Off")}
          {config.bPerformanceWeekRelMin === true
            ? renderParam("Weekly performance: Relative lower limit", getDisplayValue("dPerformanceWeekRelMin"))
            : renderParam("Weekly performance: Relative lower limit", "Off")}
          {config.bPerformanceWeekRelMax === true
            ? renderParam("Weekly performance: Relative upper limit", getDisplayValue("dPerformanceWeekRelMax"))
            : renderParam("Weekly performance: Relative upper limit", "Off")}

          {/* Monthly performance */}
          {config.bPerformanceMonthAbsMin === true
            ? renderParam("Monthly performance: Absolute lower limit", getDisplayValue("dPerformanceMonthAbsMin"))
            : renderParam("Monthly performance: Absolute lower limit", "Off")}
          {config.bPerformanceMonthAbsMax === true
            ? renderParam("Monthly performance: Absolute upper limit", getDisplayValue("dPerformanceMonthAbsMax"))
            : renderParam("Monthly performance: Absolute upper limit", "Off")}
          {config.bPerformanceMonthRelMin === true
            ? renderParam("Monthly performance: Relative lower limit", getDisplayValue("dPerformanceMonthRelMin"))
            : renderParam("Monthly performance: Relative lower limit", "Off")}
          {config.bPerformanceMonthRelMax === true
            ? renderParam("Monthly performance: Relative upper limit", getDisplayValue("dPerformanceMonthRelMax"))
            : renderParam("Monthly performance: Relative upper limit", "Off")}

          {/* Yearly performance */}
          {config.bPerformanceYearAbsMin === true
            ? renderParam("Yearly performance: Absolute lower limit", getDisplayValue("dPerformanceYearAbsMin"))
            : renderParam("Yearly performance: Absolute lower limit", "Off")}
          {config.bPerformanceYearAbsMax === true
            ? renderParam("Yearly performance: Absolute upper limit", getDisplayValue("dPerformanceYearAbsMax"))
            : renderParam("Yearly performance: Absolute upper limit", "Off")}
          {config.bPerformanceYearRelMin === true
            ? renderParam("Yearly performance: Relative lower limit", getDisplayValue("dPerformanceYearRelMin"))
            : renderParam("Yearly performance: Relative lower limit", "Off")}
          {config.bPerformanceYearRelMax === true
            ? renderParam("Yearly performance: Relative upper limit", getDisplayValue("dPerformanceYearRelMax"))
            : renderParam("Yearly performance: Relative upper limit", "Off")}

          {/* Performance Since Start */}
          {config.bPerformanceSinceStartAbsMin === true
            ? renderParam("Overall performance: Absolute lower limit", getDisplayValue("dPerformanceSinceStartAbsMin"))
            : renderParam("Overall performance: Absolute lower limit", "Off")}
          {config.bPerformanceSinceStartAbsMax === true
            ? renderParam("Overall performance: Absolute upper limit", getDisplayValue("dPerformanceSinceStartAbsMax"))
            : renderParam("Overall performance: Absolute upper limit", "Off")}
          {config.bPerformanceSinceStartRelMin === true
            ? renderParam("Overall performance: Relative lower limit", getDisplayValue("dPerformanceSinceStartRelMin"))
            : renderParam("Overall performance: Relative lower limit", "Off")}
          {config.bPerformanceSinceStartRelMax === true
            ? renderParam("Overall performance: Relative upper limit", getDisplayValue("dPerformanceSinceStartRelMax"))
            : renderParam("Overall performance: Relative upper limit", "Off")}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {/* Drawdown parameters */}
          {config.bDrawdownDayAbsMax === true
            ? renderParam("Daily drawdown : Absolute upper limit", getDisplayValue("dDrawdownDayAbsMax"))
            : renderParam("Daily drawdown : Absolute upper limit", "Off")}
          {config.bDrawdownDayRelMax === true
            ? renderParam("Daily drawdown : Relative upper limit", getDisplayValue("dDrawdownDayRelMax"))
            : renderParam("Daily drawdown : Relative upper limit", "Off")}

          {config.bDrawdownWeekAbsMax === true
            ? renderParam("Weekly drawdown : Absolute upper limit", getDisplayValue("dDrawdownWeekAbsMax"))
            : renderParam("Weekly drawdown : Absolute upper limit", "Off")}
          {config.bDrawdownWeekRelMax === true
            ? renderParam("Weekly drawdown : Relative upper limit", getDisplayValue("dDrawdownWeekRelMax"))
            : renderParam("Weekly drawdown : Relative upper limit", "Off")}

          {config.bDrawdownMonthAbsMax === true
            ? renderParam("Monthly drawdown : Absolute upper limit", getDisplayValue("dDrawdownMonthAbsMax"))
            : renderParam("Monthly drawdown : Absolute upper limit", "Off")}
          {config.bDrawdownMonthRelMax === true
            ? renderParam("Monthly drawdown : Relative upper limit", getDisplayValue("dDrawdownMonthRelMax"))
            : renderParam("Monthly drawdown : Relative upper limit", "Off")}

          {config.bDrawdownYearAbsMax === true
            ? renderParam("Yearly drawdown : Absolute upper limit", getDisplayValue("dDrawdownYearAbsMax"))
            : renderParam("Yearly drawdown : Absolute upper limit", "Off")}
          {config.bDrawdownYearRelMax === true
            ? renderParam("Yearly drawdown : Relative upper limit", getDisplayValue("dDrawdownYearRelMax"))
            : renderParam("Yearly drawdown : Relative upper limit", "Off")}

          {config.bDrawdownSinceStartAbsMax === true
            ? renderParam("Overall drawdown: Absolute upper limit", getDisplayValue("dDrawdownSinceStartAbsMax"))
            : renderParam("Overall drawdown: Absolute upper limit", "Off")}
          {config.bDrawdownSinceStartRelMax === true
            ? renderParam("Overall drawdown: Relative upper limit", getDisplayValue("dDrawdownSinceStartRelMax"))
            : renderParam("Overall drawdown: Relative upper limit", "Off")}
        </>
      ) : null}

      {professionalMode === false ? (
        <>
          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {/* Performance Since Start - Only for non-professional mode */}
          {config.bPerformanceSinceStartAbsMin === true
            ? renderParam("Performance: Absolute lower limit", getDisplayValue("dPerformanceSinceStartAbsMin"))
            : renderParam("Performance: Absolute lower limit", "Off")}
          {config.bPerformanceSinceStartAbsMax === true
            ? renderParam("Performance: Absolute upper limit", getDisplayValue("dPerformanceSinceStartAbsMax"))
            : renderParam("Performance: Absolute upper limit", "Off")}
          {config.bPerformanceSinceStartRelMin === true
            ? renderParam("Performance: Relative lower limit", getDisplayValue("dPerformanceSinceStartRelMin"))
            : renderParam("Performance: Relative lower limit", "Off")}
          {config.bPerformanceSinceStartRelMax === true
            ? renderParam("Performance: Relative upper limit", getDisplayValue("dPerformanceSinceStartRelMax"))
            : renderParam("Performance: Relative upper limit", "Off")}

          <Box>
            <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          </Box>

          {/* Drawdown Since Start - Only for non-professional mode */}
          {config.bDrawdownSinceStartAbsMax === true
            ? renderParam("Overall drawdown: Absolute upper limit", getDisplayValue("dDrawdownSinceStartAbsMax"))
            : renderParam("Overall drawdown: Absolute upper limit", "Off")}
          {config.bDrawdownSinceStartRelMax === true
            ? renderParam("Overall drawdown: Relative upper limit", getDisplayValue("dDrawdownSinceStartRelMax"))
            : renderParam("Overall drawdown: Relative upper limit", "Off")}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const MonitoringConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  MonitoringConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  const [collapsePanel, setCollapsePanel] = useState(false);
  const [collapsePerformance, setCollapsePerformance] = useState(false);
  const [collapseDrawdown, setCollapseDrawdown] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(MonitoringDefaults).forEach((key) => {
      setFieldValue(key, MonitoringDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nDrawInspectorPanel: initialData?.nDrawInspectorPanel ?? MonitoringDefaults.nDrawInspectorPanel,
      //
      dPerformanceDayAbsMin: initialData?.dPerformanceDayAbsMin ?? MonitoringDefaults.dPerformanceDayAbsMin,
      dPerformanceDayAbsMax: initialData?.dPerformanceDayAbsMax ?? MonitoringDefaults.dPerformanceDayAbsMax,
      dPerformanceDayRelMin: initialData?.dPerformanceDayRelMin ?? MonitoringDefaults.dPerformanceDayRelMin,
      dPerformanceDayRelMax: initialData?.dPerformanceDayRelMax ?? MonitoringDefaults.dPerformanceDayRelMax,
      dPerformanceWeekAbsMin: initialData?.dPerformanceWeekAbsMin ?? MonitoringDefaults.dPerformanceWeekAbsMin,
      dPerformanceWeekAbsMax: initialData?.dPerformanceWeekAbsMax ?? MonitoringDefaults.dPerformanceWeekAbsMax,
      dPerformanceWeekRelMin: initialData?.dPerformanceWeekRelMin ?? MonitoringDefaults.dPerformanceWeekRelMin,
      dPerformanceWeekRelMax: initialData?.dPerformanceWeekRelMax ?? MonitoringDefaults.dPerformanceWeekRelMax,
      dPerformanceMonthAbsMin: initialData?.dPerformanceMonthAbsMin ?? MonitoringDefaults.dPerformanceMonthAbsMin,
      dPerformanceMonthAbsMax: initialData?.dPerformanceMonthAbsMax ?? MonitoringDefaults.dPerformanceMonthAbsMax,
      dPerformanceMonthRelMin: initialData?.dPerformanceMonthRelMin ?? MonitoringDefaults.dPerformanceMonthRelMin,
      dPerformanceMonthRelMax: initialData?.dPerformanceMonthRelMax ?? MonitoringDefaults.dPerformanceMonthRelMax,
      dPerformanceYearAbsMin: initialData?.dPerformanceYearAbsMin ?? MonitoringDefaults.dPerformanceYearAbsMin,
      dPerformanceYearAbsMax: initialData?.dPerformanceYearAbsMax ?? MonitoringDefaults.dPerformanceYearAbsMax,
      dPerformanceYearRelMin: initialData?.dPerformanceYearRelMin ?? MonitoringDefaults.dPerformanceYearRelMin,
      dPerformanceYearRelMax: initialData?.dPerformanceYearRelMax ?? MonitoringDefaults.dPerformanceYearRelMax,
      dPerformanceSinceStartAbsMin: initialData?.dPerformanceSinceStartAbsMin ?? MonitoringDefaults.dPerformanceSinceStartAbsMin,
      dPerformanceSinceStartAbsMax: initialData?.dPerformanceSinceStartAbsMax ?? MonitoringDefaults.dPerformanceSinceStartAbsMax,
      dPerformanceSinceStartRelMin: initialData?.dPerformanceSinceStartRelMin ?? MonitoringDefaults.dPerformanceSinceStartRelMin,
      dPerformanceSinceStartRelMax: initialData?.dPerformanceSinceStartRelMax ?? MonitoringDefaults.dPerformanceSinceStartRelMax,
      //
      dDrawdownDayAbsMax: initialData?.dDrawdownDayAbsMax ?? MonitoringDefaults.dDrawdownDayAbsMax,
      dDrawdownDayRelMax: initialData?.dDrawdownDayRelMax ?? MonitoringDefaults.dDrawdownDayRelMax,
      dDrawdownWeekAbsMax: initialData?.dDrawdownWeekAbsMax ?? MonitoringDefaults.dDrawdownWeekAbsMax,
      dDrawdownWeekRelMax: initialData?.dDrawdownWeekRelMax ?? MonitoringDefaults.dDrawdownWeekRelMax,
      dDrawdownMonthAbsMax: initialData?.dDrawdownMonthAbsMax ?? MonitoringDefaults.dDrawdownMonthAbsMax,
      dDrawdownMonthRelMax: initialData?.dDrawdownMonthRelMax ?? MonitoringDefaults.dDrawdownMonthRelMax,
      dDrawdownYearAbsMax: initialData?.dDrawdownYearAbsMax ?? MonitoringDefaults.dDrawdownYearAbsMax,
      dDrawdownYearRelMax: initialData?.dDrawdownYearRelMax ?? MonitoringDefaults.dDrawdownYearRelMax,
      dDrawdownSinceStartAbsMax: initialData?.dDrawdownSinceStartAbsMax ?? MonitoringDefaults.dDrawdownSinceStartAbsMax,
      dDrawdownSinceStartRelMax: initialData?.dDrawdownSinceStartRelMax ?? MonitoringDefaults.dDrawdownSinceStartRelMax,
      // Enable switches
      bPerformanceDayAbsMin: initialData?.bPerformanceDayAbsMin ?? MonitoringDefaults.bPerformanceDayAbsMin,
      bPerformanceDayAbsMax: initialData?.bPerformanceDayAbsMax ?? MonitoringDefaults.bPerformanceDayAbsMax,
      bPerformanceDayRelMin: initialData?.bPerformanceDayRelMin ?? MonitoringDefaults.bPerformanceDayRelMin,
      bPerformanceDayRelMax: initialData?.bPerformanceDayRelMax ?? MonitoringDefaults.bPerformanceDayRelMax,
      bPerformanceWeekAbsMin: initialData?.bPerformanceWeekAbsMin ?? MonitoringDefaults.bPerformanceWeekAbsMin,
      bPerformanceWeekAbsMax: initialData?.bPerformanceWeekAbsMax ?? MonitoringDefaults.bPerformanceWeekAbsMax,
      bPerformanceWeekRelMin: initialData?.bPerformanceWeekRelMin ?? MonitoringDefaults.bPerformanceWeekRelMin,
      bPerformanceWeekRelMax: initialData?.bPerformanceWeekRelMax ?? MonitoringDefaults.bPerformanceWeekRelMax,
      bPerformanceMonthAbsMin: initialData?.bPerformanceMonthAbsMin ?? MonitoringDefaults.bPerformanceMonthAbsMin,
      bPerformanceMonthAbsMax: initialData?.bPerformanceMonthAbsMax ?? MonitoringDefaults.bPerformanceMonthAbsMax,
      bPerformanceMonthRelMin: initialData?.bPerformanceMonthRelMin ?? MonitoringDefaults.bPerformanceMonthRelMin,
      bPerformanceMonthRelMax: initialData?.bPerformanceMonthRelMax ?? MonitoringDefaults.bPerformanceMonthRelMax,
      bPerformanceYearAbsMin: initialData?.bPerformanceYearAbsMin ?? MonitoringDefaults.bPerformanceYearAbsMin,
      bPerformanceYearAbsMax: initialData?.bPerformanceYearAbsMax ?? MonitoringDefaults.bPerformanceYearAbsMax,
      bPerformanceYearRelMin: initialData?.bPerformanceYearRelMin ?? MonitoringDefaults.bPerformanceYearRelMin,
      bPerformanceYearRelMax: initialData?.bPerformanceYearRelMax ?? MonitoringDefaults.bPerformanceYearRelMax,
      bPerformanceSinceStartAbsMin: initialData?.bPerformanceSinceStartAbsMin ?? MonitoringDefaults.bPerformanceSinceStartAbsMin,
      bPerformanceSinceStartAbsMax: initialData?.bPerformanceSinceStartAbsMax ?? MonitoringDefaults.bPerformanceSinceStartAbsMax,
      bPerformanceSinceStartRelMin: initialData?.bPerformanceSinceStartRelMin ?? MonitoringDefaults.bPerformanceSinceStartRelMin,
      bPerformanceSinceStartRelMax: initialData?.bPerformanceSinceStartRelMax ?? MonitoringDefaults.bPerformanceSinceStartRelMax,
      bDrawdownDayAbsMax: initialData?.bDrawdownDayAbsMax ?? MonitoringDefaults.bDrawdownDayAbsMax,
      bDrawdownDayRelMax: initialData?.bDrawdownDayRelMax ?? MonitoringDefaults.bDrawdownDayRelMax,
      bDrawdownWeekAbsMax: initialData?.bDrawdownWeekAbsMax ?? MonitoringDefaults.bDrawdownWeekAbsMax,
      bDrawdownWeekRelMax: initialData?.bDrawdownWeekRelMax ?? MonitoringDefaults.bDrawdownWeekRelMax,
      bDrawdownMonthAbsMax: initialData?.bDrawdownMonthAbsMax ?? MonitoringDefaults.bDrawdownMonthAbsMax,
      bDrawdownMonthRelMax: initialData?.bDrawdownMonthRelMax ?? MonitoringDefaults.bDrawdownMonthRelMax,
      bDrawdownYearAbsMax: initialData?.bDrawdownYearAbsMax ?? MonitoringDefaults.bDrawdownYearAbsMax,
      bDrawdownYearRelMax: initialData?.bDrawdownYearRelMax ?? MonitoringDefaults.bDrawdownYearRelMax,
      bDrawdownSinceStartAbsMax: initialData?.bDrawdownSinceStartAbsMax ?? MonitoringDefaults.bDrawdownSinceStartAbsMax,
      bDrawdownSinceStartRelMax: initialData?.bDrawdownSinceStartRelMax ?? MonitoringDefaults.bDrawdownSinceStartRelMax,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
    formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // Helper function to render parameter with switch and conditional input
  const renderParameterWithSwitch = (switchField, valueField, label, defaultValue = "0") => (
    <Box>
      <SmartParameter
        value={label}
        helpReference="HandleMonitoring"
        helpText=""
        helpLink=""
        fieldName={valueField}
        touched={touched}
        errors={errors}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <ModalContentSwitch
          sx={{ my: "12px" }}
          checked={values[switchField] === true}
          onChange={(event) => {
            setFieldValue(switchField, event.target.checked);
          }}
        />
        {values[switchField] === true ? <SmartNumberInput textFieldName={valueField} defaultLimits={DEFAULT_LIMITS} formik={formik} /> : null}
      </Box>
    </Box>
  );

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure monitoring
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure monitoring</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nDrawInspectorPanel */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Draw inspector panel"
                      helpReference="HandleMonitoring"
                      helpText=""
                      helpLink=""
                      fieldName="nDrawInspectorPanel"
                      touched={touched}
                      errors={errors}
                    />
                    <ModalContentSwitch
                      sx={{ mt: "12px" }}
                      checked={values.nDrawInspectorPanel === true}
                      onChange={(event) => {
                        setFieldValue("nDrawInspectorPanel", event.target.checked);
                      }}
                    />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {MONITORING_PANEL_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapsePanel(!collapsePanel);
                        }}
                      >
                        {collapsePanel ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapsePanel} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {MONITORING_PANEL_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "#DCDCDC",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={MONITORING_PANEL_HELP_IMAGE}
                            alt="Inspector panel"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "486px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Headline */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Typography variant="h3" sx={{ mt: 0.5 }}>
                      Performance monitoring
                    </Typography>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {MONITORING_PERFORMANCE_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapsePerformance(!collapsePerformance);
                        }}
                      >
                        {collapsePerformance ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapsePerformance} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {MONITORING_PERFORMANCE_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={MONITORING_PERFORMANCE_HELP_IMAGE}
                            alt="Performance monitoring"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "387px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {professionalMode === true ? (
                    <>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceDayAbsMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceDayAbsMin", "dPerformanceDayAbsMin", "Daily performance: Absolute lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceDayAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceDayAbsMax", "dPerformanceDayAbsMax", "Daily performance: Absolute upper limit")}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceDayRelMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceDayRelMin", "dPerformanceDayRelMin", "Daily performance: Relative lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceDayRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceDayRelMax", "dPerformanceDayRelMax", "Daily performance: Relative upper limit")}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceWeekAbsMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceWeekAbsMin", "dPerformanceWeekAbsMin", "Weekly performance: Absolute lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceWeekAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceWeekAbsMax", "dPerformanceWeekAbsMax", "Weekly performance: Absolute upper limit")}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceWeekRelMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceWeekRelMin", "dPerformanceWeekRelMin", "Weekly performance: Relative lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceWeekRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceWeekRelMax", "dPerformanceWeekRelMax", "Weekly performance: Relative upper limit")}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceMonthAbsMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceMonthAbsMin", "dPerformanceMonthAbsMin", "Monthly performance: Absolute lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceMonthAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceMonthAbsMax", "dPerformanceMonthAbsMax", "Monthly performance: Absolute upper limit")}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceMonthRelMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceMonthRelMin", "dPerformanceMonthRelMin", "Monthly performance: Relative lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceMonthRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceMonthRelMax", "dPerformanceMonthRelMax", "Monthly performance: Relative upper limit")}
                      </Box>

                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceYearAbsMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceYearAbsMin", "dPerformanceYearAbsMin", "Yearly performance: Absolute lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceYearAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceYearAbsMax", "dPerformanceYearAbsMax", "Yearly performance: Absolute upper limit")}
                      </Box>
                      {/* ---------------------------------------------------------------------- */}
                      {/* Grid */}
                      {/* ---------------------------------------------------------------------- */}
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceYearRelMin */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceYearRelMin", "dPerformanceYearRelMin", "Yearly performance: Relative lower limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dPerformanceYearRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bPerformanceYearRelMax", "dPerformanceYearRelMax", "Yearly performance: Relative upper limit")}
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dPerformanceSinceStartAbsMin */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch(
                      "bPerformanceSinceStartAbsMin",
                      "dPerformanceSinceStartAbsMin",
                      "Overall performance: Absolute lower limit"
                    )}
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dPerformanceSinceStartAbsMax */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch(
                      "bPerformanceSinceStartAbsMax",
                      "dPerformanceSinceStartAbsMax",
                      "Overall performance: Absolute upper limit"
                    )}
                  </Box>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dPerformanceSinceStartRelMin */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch(
                      "bPerformanceSinceStartRelMin",
                      "dPerformanceSinceStartRelMin",
                      "Overall performance: Relative lower limit"
                    )}
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dPerformanceSinceStartRelMax */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch(
                      "bPerformanceSinceStartRelMax",
                      "dPerformanceSinceStartRelMax",
                      "Overall performance: Relative upper limit"
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Headline */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Typography variant="h3" sx={{ mt: 0.5 }}>
                      Drawdown monitoring
                    </Typography>
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {MONITORING_DRAWDOWN_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapseDrawdown(!collapseDrawdown);
                        }}
                      >
                        {collapseDrawdown ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapseDrawdown} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {MONITORING_DRAWDOWN_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={MONITORING_DRAWDOWN_HELP_IMAGE}
                            alt="Drawdown monitoring"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "387px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {professionalMode === true ? (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownDayAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownDayAbsMax", "dDrawdownDayAbsMax", "Daily drawdown : Absolute upper limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownDayRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownDayRelMax", "dDrawdownDayRelMax", "Daily drawdown : Relative upper limit")}
                      </Box>

                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownWeekAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownWeekAbsMax", "dDrawdownWeekAbsMax", "Weekly drawdown : Absolute upper limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownWeekRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownWeekRelMax", "dDrawdownWeekRelMax", "Weekly drawdown : Relative upper limit")}
                      </Box>

                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownMonthAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownMonthAbsMax", "dDrawdownMonthAbsMax", "Monthly drawdown : Absolute upper limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownMonthRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownMonthRelMax", "dDrawdownMonthRelMax", "Monthly drawdown : Relative upper limit")}
                      </Box>

                      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownYearAbsMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownYearAbsMax", "dDrawdownYearAbsMax", "Yearly drawdown : Absolute upper limit")}
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* dDrawdownYearRelMax */}
                        {/* ---------------------------------------------------------------------- */}
                        {renderParameterWithSwitch("bDrawdownYearRelMax", "dDrawdownYearRelMax", "Yearly drawdown : Relative upper limit")}
                      </Box>
                    </>
                  ) : null}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dDrawdownSinceStartAbsMax */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch("bDrawdownSinceStartAbsMax", "dDrawdownSinceStartAbsMax", "Overall drawdown: Absolute upper limit")}
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* dDrawdownSinceStartRelMax */}
                    {/* ---------------------------------------------------------------------- */}
                    {renderParameterWithSwitch("bDrawdownSinceStartRelMax", "dDrawdownSinceStartRelMax", "Overall drawdown: Relative upper limit")}
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
